import type { ReviewSearchParamsDto } from '@/contracts/marketplace/reviews';
import type { ActiveTable } from '@/components/reviews-client-page/reviews-client-page.types';
import type { SortDirection } from '@/enums/sort';
import type { BrandGroupEntity } from '@/entities/brand-group.entity';
import type { ProductGroupEntity } from '@/entities/product-group.entity';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

export type ReviewsClientPageControllerBindings = {
    total: number;
    products: ProductGroupEntity[];
    brandGroups: BrandGroupEntity[];
    filters: ReviewSearchParamsDto;
    reviewsPage: number;
    reviewsOnPage: number;
    setReviewsOnPage: (count: number) => Promise<void>;
    activeTable: ActiveTable;
    setActiveTable: Dispatch<SetStateAction<ActiveTable>>;
    productsPage: number;
    setProductsPage: Dispatch<SetStateAction<number>>;
    productsOnPage: number;
    productsPageCount: number;
    setProductsPageCount: Dispatch<SetStateAction<number>>;
    productsTotalRows: number;
    setProductsTotalRows: Dispatch<SetStateAction<number>>;
    brandsPage: number;
    setBrandsPage: Dispatch<SetStateAction<number>>;
    brandsOnPage: number;
    brandsPageCount: number;
    setBrandsPageCount: Dispatch<SetStateAction<number>>;
    brandsTotalRows: number;
    setBrandsTotalRows: Dispatch<SetStateAction<number>>;
    rowsPerPagePresets: number[];
    scrollContainerRef: MutableRefObject<HTMLDivElement | null>;
    topControlsContainerRef: MutableRefObject<HTMLDivElement | null>;
    onSortChanged: (column: string, direction?: SortDirection) => void;
    updateFilters: (nextFilters: SetStateAction<ReviewSearchParamsDto>) => void;
    setPage: (nextPage: number) => void;
    onProductsOnPageUpdate: (count: number) => void;
    onBrandsOnPageUpdate: (count: number) => void;
};

type ReviewsClientPageDataBindings = Pick<
    ReviewsClientPageControllerBindings,
    'total' | 'products' | 'brandGroups' | 'filters'
>;

type ReviewsClientPagePaginationBindings = Pick<
    ReviewsClientPageControllerBindings,
    | 'reviewsOnPage'
    | 'reviewsPage'
    | 'setReviewsOnPage'
    | 'activeTable'
    | 'setActiveTable'
    | 'productsPage'
    | 'setProductsPage'
    | 'productsOnPage'
    | 'productsPageCount'
    | 'setProductsPageCount'
    | 'productsTotalRows'
    | 'setProductsTotalRows'
    | 'brandsPage'
    | 'setBrandsPage'
    | 'brandsOnPage'
    | 'brandsPageCount'
    | 'setBrandsPageCount'
    | 'brandsTotalRows'
    | 'setBrandsTotalRows'
    | 'onProductsOnPageUpdate'
    | 'onBrandsOnPageUpdate'
>;

type ReviewsClientPageLayoutBindings = Pick<
    ReviewsClientPageControllerBindings,
    'rowsPerPagePresets' | 'scrollContainerRef' | 'topControlsContainerRef'
>;

type ReviewsClientPageQueryActionBindings = Pick<
    ReviewsClientPageControllerBindings,
    'onSortChanged' | 'updateFilters' | 'setPage'
>;

type BuildReviewsClientPageControllerBindingsArgs = {
    data: ReviewsClientPageDataBindings;
    pagination: ReviewsClientPagePaginationBindings;
    layout: ReviewsClientPageLayoutBindings;
    queryActions: ReviewsClientPageQueryActionBindings;
};

export const buildReviewsClientPageControllerBindings = ({
    data,
    pagination,
    layout,
    queryActions,
}: BuildReviewsClientPageControllerBindingsArgs): ReviewsClientPageControllerBindings => ({
    ...data,
    ...pagination,
    ...layout,
    ...queryActions,
});
